import React, { useRef, useEffect, useState } from 'react';
import MarkdownRenderer from 'components/markdown/MarkdownRenderer';

function IntroModal({ isOpen, onClose, content }) {
    const scrollRef = useRef(null);
    const modalRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    // Handle modal open/close with animations
    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            // Delay visibility for smooth animation
            setTimeout(() => setIsVisible(true), 10);
        } else {
            setIsVisible(false);
            // Wait for fade out animation before unmounting
            const timer = setTimeout(() => setShouldRender(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Close modal on ESC key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent background scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Close modal on click outside
    const handleBackdropClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    if (!shouldRender) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={handleBackdropClick}
            style={{
                backgroundColor: isVisible ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
                backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
                transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease'
            }}
        >
            <div
                ref={modalRef}
                className="relative bg-background border-4 border-border rounded-3xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
                style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
                    transition: 'opacity 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
            >
                {/* Header with close button */}
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-border">
                    <h2 className="text-2xl font-bold text-text">Concept Intro</h2>
                    <button
                        onClick={onClose}
                        className="text-text hover:text-accent transition-colors text-4xl font-normal w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-light pb-2"
                        aria-label="Close modal"
                        style={{ lineHeight: '1' }}
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable content */}
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto px-6 py-6"
                >
                    <div className="text-left text-2xl">
                        <MarkdownRenderer content={content || "No introduction content available."} />
                    </div>
                </div>

                {/* Footer with hint */}
                <div className="px-6 py-3 border-t-2 border-border text-center text-sm text-text opacity-60">
                    Press <kbd className="px-2 py-1 bg-background-light rounded border border-border">ESC</kbd> to close
                </div>
            </div>
        </div>
    );
}

export default IntroModal;
